import { useState, useRef, useEffect } from 'react';
import { Check, ChevronsUpDown, Plus, Trash2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

export function CategoryCombobox({ 
  value, 
  categories, 
  onChange, 
  onCreateCategory,
  onDeleteCategory,
  disabled 
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const handleSelect = (selectedValue) => {
    onChange(selectedValue);
    setOpen(false);
    setInputValue('');
  };

  const handleCreate = async () => {
    if (!inputValue.trim()) return;
    
    const newCategory = await onCreateCategory(inputValue.trim());
    if (newCategory) {
      onChange(newCategory.name);
      setOpen(false);
      setInputValue('');
    }
  };

  const handleDelete = async (category) => {
    if (!onDeleteCategory) return;
    const msg = `Remove "${category.name}" from the category list? Existing line items keep this label until you change them.`;
    if (!window.confirm(msg)) return;
    await onDeleteCategory(category);
  };

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(inputValue.toLowerCase())
  );

  const exactMatch = categories.some(
    cat => cat.name.toLowerCase() === inputValue.toLowerCase()
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between h-7 px-2 text-sm font-normal border-transparent hover:border-gray-300 bg-transparent"
          data-testid="category-combobox"
        >
          <span className={cn(!value && "text-gray-400")}>
            {value || "Category..."}
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command>
          <CommandInput 
            placeholder="Search or create..." 
            value={inputValue}
            onValueChange={setInputValue}
            data-testid="category-search-input"
          />
          <CommandList>
            <CommandEmpty>
              {inputValue && !exactMatch && (
                <button
                  onClick={handleCreate}
                  className="flex items-center gap-2 w-full px-2 py-1.5 text-sm hover:bg-gray-100 cursor-pointer"
                  data-testid="create-category-btn"
                >
                  <Plus className="h-4 w-4" />
                  Create "{inputValue}"
                </button>
              )}
              {!inputValue && <span className="text-gray-500 text-sm px-2">Type to search or create</span>}
            </CommandEmpty>
            <CommandGroup>
              {filteredCategories.map((cat) => (
                <CommandItem
                  key={cat.id}
                  value={cat.name}
                  onSelect={() => handleSelect(cat.name)}
                  className="group flex items-center justify-between gap-1 pr-1"
                  data-testid={`category-option-${cat.name}`}
                >
                  <span className="flex min-w-0 flex-1 items-center">
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        value === cat.name ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="truncate">{cat.name}</span>
                  </span>
                  {onDeleteCategory ? (
                    <button
                      type="button"
                      className="rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 focus:opacity-100"
                      onPointerDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDelete(cat);
                      }}
                      aria-label={`Delete category ${cat.name}`}
                      data-testid={`delete-category-${cat.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </CommandItem>
              ))}
              {inputValue && !exactMatch && filteredCategories.length > 0 && (
                <CommandItem
                  onSelect={handleCreate}
                  className="border-t"
                  data-testid="create-category-option"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create "{inputValue}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
